/**
 * Attach a redacted copy of a failed vendor response to the Error being thrown,
 * so the evidence travels WITH the error to whatever reports it.
 *
 * The motivating case is GB #1527 / Sentry WEB-12S: checkout's shipping-rate
 * lookup throws a bare `Error: Invalid request`, which is all the responding
 * route puts in `error`. The route also sends a `details` payload describing
 * what failed, and the client discarded it — so 30 production events across
 * 5 users said nothing about which field was wrong.
 *
 * Why the context rides on the Error rather than going to Sentry directly:
 * `Sentry.setContext` writes one named slot on the current scope. Checkout
 * prefetches rates for every cart item CONCURRENTLY (usePrefetchRates), so
 * several failures race for that slot and the last writer wins — events would
 * be captured carrying a sibling product's body. Attaching to the Error keeps
 * each body with its own error, and `logError` passes it at capture time.
 *
 * Contract:
 * - NEVER throws. This runs on an error path; a reporting bug must not replace
 *   the real error with its own.
 * - Returns the same Error instance it was given, so callers can `throw` it
 *   inline without changing control flow.
 * - Redacts the `body` ONLY. The Error's own `message` is untouched, and that
 *   message is what Sentry shows as the exception value and the issue title. A
 *   call site that puts vendor text in the message has not protected it by
 *   calling this function.
 *
 * What redaction does and does NOT cover — read this before adding a caller:
 * - COVERED: values under keys that look like credentials or personal data
 *   (CREDENTIAL_KEY / PII_KEY), and four free-text shapes inside any surviving
 *   string value — emails, `Bearer` tokens, card-shaped digit runs, and long
 *   opaque tokens.
 * - NOT COVERED: personal data written as PROSE. A name, street, city, or
 *   postal code sitting in a sentence — `"no rates to 742 Evergreen Terrace,
 *   Springfield"` — matches none of the four text patterns and is reported
 *   verbatim. Numeric values escape both tiers, camelCase keys (`firstName`)
 *   dodge the key list, and JSON-inside-a-string bypasses the key tier
 *   entirely.
 *
 * The consequence for the obvious next caller — "capture the actual ShipEngine
 * response body" — is that a carrier's free-text validation message is NOT made
 * safe by passing it through here. Carrier prose is composed from the shipment
 * we submitted, so it is exactly the prose case above. Forward the vendor's
 * STRUCTURED fields (error_code, field_name) instead; the key tier protects
 * those, and see the rate_response.errors branch in
 * src/pages/api/checkout/shipping-rates.ts for the precedent. A key denylist
 * cannot be completed — an allowlist is the real answer if a caller ever needs
 * whole vendor bodies.
 */

/** Serialized body is capped at 2KB; Sentry drops oversized contexts. */
const MAX_BODY_CHARS = 2048;

/**
 * Ceilings on the redaction walk, shared across the WHOLE body rather than
 * per-node, because the dangerous shapes are wide/deep rather than individually
 * large.
 *
 * Why a node ceiling: cycle detection below is a current-PATH set (`seen` is
 * popped in a `finally`), which is what lets a legitimately shared child render
 * twice instead of being mislabelled `[circular]`. The cost is that a
 * shared-reference graph is re-walked once per path that reaches it — that is
 * breadth^depth, and the breadth (50) and depth (6) limits multiply out to
 * ~1.5e10 visits. Measured on this module: 14 keys at depth 6 (7.5e6 visits)
 * already takes 4.5s of blocked main thread, so the full 50 is hours.
 * `JSON.parse` cannot emit shared references, so no vendor body reaches this
 * today — but the function is exported and takes `body?: unknown`, so a future
 * caller handing it a live object graph must degrade to a marker, not a hang.
 *
 * Why a character ceiling: strings are now scrubbed at full length BEFORE being
 * capped (see the string branch), so total regex work is proportional to total
 * input rather than to the 2KB output.
 */
const MAX_NODES = 10_000;
const MAX_SCRUB_CHARS = 256 * 1024;

/** Mutable walk budget, threaded through every `redact` call for one body. */
type RedactBudget = { nodes: number; chars: number };

/** Property the context is stashed on. Read by logError at capture time. */
export const VENDOR_ERROR_PROP = '__vendorError';

/**
 * Keys whose values are secrets. Never diagnostic, so replaced unconditionally.
 */
const CREDENTIAL_KEY =
  /(token|api[-_]?key|^key$|secret|password|passwd|authorization|^auth$|credential|session|cookie|bearer)/i;

/**
 * Keys whose values are personal data. The KEY survives — knowing that
 * `ship_to.postal_code` was the field at fault is the diagnosis — the value
 * does not.
 */
const PII_KEY =
  /(email|phone|^name$|_name$|^name_|address|street|city|locality|region|province|postal|zip|country|recipient|contact|attention|addressee|company|customer|buyer|seller|user|household|^to$|^line\d|ssn|tax[-_]?id|dob|birth|card|cvv|cvc|^pan$|iban|account[-_]?number|field[-_]?value)/i;

/**
 * Keys that trip the patterns above but are schema identifiers, not data.
 *
 * `field_name` is the field that failed validation ('postal_code') in a carrier
 * error — the single most useful token in a rate-validation failure — and
 * `_name$` in PII_KEY was replacing it with '[redacted]', deleting the answer
 * while keeping the question. An allowlist is the right shape here rather than
 * loosening `_name$`, which still has to cover first_name / last_name /
 * full_name / business_name.
 *
 * Note the deliberate asymmetry: `field_value` — the offending value itself, so
 * a real postal code or phone number — was added to PII_KEY in the same change.
 * Exempting the name without covering the value would have been a net leak.
 */
const SAFE_KEY = /^field[-_]?name$/i;

/** Free-text patterns scrubbed from any surviving string value. */
const TEXT_PATTERNS: Array<[RegExp, string]> = [
  // ANCHORED to the start of the local part, via a captured preceding character
  // that the replacement puts back. Three reasons, and they are linked:
  //
  // 1. Without an anchor, a local part longer than the bound matched only its
  //    LAST `bound` characters and the prefix survived: a 299-char dotted local
  //    part leaked 235 characters of itself (measured). Anchoring means the
  //    match either covers the whole local part or does not fire at all.
  // 2. "Does not fire at all" would be worse than a prefix leak, so the bound
  //    has to be generous — and anchoring is exactly what makes a generous
  //    bound affordable. Unanchored `{1,320}@` retries at every offset inside a
  //    long `@`-free run (quadratic; 16s of frozen checkout tab at 200KB).
  //    Anchored, only one offset per run can start a match, so the work is
  //    linear in the body regardless of the bound. 320 clears the RFC 5321 limit
  //    of 64 octets with room for an address that merely looks absurd.
  // 3. The two classes are EXACT COMPLEMENTS, and that is what makes the anchor
  //    safe. The anchor set is the RFC 5322 "specials" — the characters an
  //    unquoted local part cannot contain — minus `.`, which dot-atoms need. The
  //    local class is everything else, so it covers apostrophes (O'Brien.Jane),
  //    every other RFC-legal special, and non-ASCII (josé). Under the previous
  //    `[\w.+-]` class those characters were anchors instead of local-part
  //    material, which is precisely how `john'smith@x.com` leaked `john'` and
  //    `josé@x.com` failed to match at all. Because the sets are complements, a
  //    match always begins at the true start of the local part: there is no
  //    character that can both sit inside a local part and end an anchor.
  //
  // Quoted local parts (`"jane doe"@x.com`) are still missed — the quote is an
  // anchor and the space inside is not local-part material. RFC-legal,
  // vanishingly rare in practice, and no worse than the previous pattern.
  //
  // A capture group rather than a lookbehind: lookbehind is a SyntaxError at
  // parse time on Safari < 16.4, and an unparseable regex in this module takes
  // down `logError`, which is imported app-wide. See the same decision at
  // src/utils/text/simpleMarkdown.tsx:96.
  [
    /(^|[\s()<>[\]:;@\\,"])[^\s()<>[\]:;@\\,"]{1,320}@[\w-]{1,255}\.[\w.-]{2,64}/g,
    '$1[email]',
  ],
  [/\bBearer\s+[\w.\-~+/]{1,512}=*/gi, '[token]'],
  // Card-shaped: 13-19 digits, optionally spaced or dashed.
  [/\b(?:\d[ -]?){13,19}\b/g, '[card]'],
  // Long opaque strings: API keys, JWTs, session ids. Deliberately NOT
  // `\b...{32,128}\b` — inside an unbroken run longer than 128 chars every
  // candidate end position sits between two word characters, so the boundary
  // never matches and the longest secrets were the ones passing through. No
  // upper bound and no leading `\b` keeps it linear and greedy.
  [/[A-Za-z0-9_-]{32,}/g, '[token]'],
];

function scrubText(value: string): string {
  return TEXT_PATTERNS.reduce(
    (acc, [pattern, replacement]) => acc.replace(pattern, replacement),
    value
  );
}

/**
 * Rebuild the body with sensitive values replaced. Depth, breadth, and string
 * length are all bounded: a vendor payload is untrusted input.
 */
function redact(
  value: unknown,
  budget: RedactBudget,
  depth = 0,
  seen = new WeakSet<object>()
): unknown {
  // Charged per visit, not per distinct node, so a re-walked shared subtree
  // still counts. This is the only thing standing between a shared-reference
  // graph and an unbounded walk.
  if (budget.nodes <= 0) return '[truncated: node budget]';
  budget.nodes -= 1;

  if (depth > 6) return '[depth-limit]';
  if (value === null || value === undefined) return value;

  if (typeof value === 'string') {
    // Scrub BEFORE capping. Capping first severs a secret that straddles the
    // boundary into a fragment shorter than the token rule's 32-char floor,
    // which then passes through verbatim.
    if (value.length > budget.chars) return '[truncated: scrub budget]';
    budget.chars -= value.length;
    const scrubbed = scrubText(value);
    return scrubbed.length > MAX_BODY_CHARS
      ? scrubbed.slice(0, MAX_BODY_CHARS)
      : scrubbed;
  }
  if (typeof value === 'number' || typeof value === 'boolean') return value;

  if (typeof value === 'object') {
    if (seen.has(value as object)) return '[circular]';
    seen.add(value as object);
    try {
      if (Array.isArray(value)) {
        const items = value
          .slice(0, 20)
          .map((v) => redact(v, budget, depth + 1, seen));
        if (value.length > 20) items.push(`[+${value.length - 20} more]`);
        return items;
      }

      // Null-prototype so a `__proto__` key in the payload is stored as data
      // instead of hitting Object.prototype's setter and silently vanishing.
      const out: Record<string, unknown> = Object.create(null);
      let keys: string[];
      try {
        // Object.keys, not Object.entries: entries reads every value up front,
        // so ONE throwing getter would take the whole object down with it.
        keys = Object.keys(value as object).slice(0, 50);
      } catch {
        return '[unreadable]';
      }
      for (const key of keys) {
        if (
          !SAFE_KEY.test(key) &&
          (CREDENTIAL_KEY.test(key) || PII_KEY.test(key))
        ) {
          out[key] = '[redacted]';
          continue;
        }
        // Contain the blast radius to the single bad key. Reading the value is
        // inside the try because that is what fires a hostile getter.
        try {
          out[key] = redact(
            (value as Record<string, unknown>)[key],
            budget,
            depth + 1,
            seen
          );
        } catch {
          out[key] = '[unreadable]';
        }
      }
      return { ...out };
    } finally {
      // Pop on exit so a value shared by two siblings is not mislabelled
      // circular; only a genuine ancestor cycle reports [circular].
      seen.delete(value as object);
    }
  }

  return '[unserializable]';
}

function serialize(body: unknown): string {
  let text: string;
  const budget: RedactBudget = { nodes: MAX_NODES, chars: MAX_SCRUB_CHARS };
  try {
    // A bare-string body goes through the same string branch as any nested
    // string, so it gets the same scrub-then-cap order and the same character
    // budget. `res.json()` yielding a raw string is a documented input here.
    const redacted = redact(body, budget);
    text = typeof redacted === 'string' ? redacted : JSON.stringify(redacted);
  } catch {
    return '[unserializable]';
  }
  if (typeof text !== 'string') return '[unserializable]';
  return text.length > MAX_BODY_CHARS
    ? `${text.slice(0, MAX_BODY_CHARS)}…[truncated ${text.length - MAX_BODY_CHARS} chars]`
    : text;
}

export type VendorErrorResponse = {
  status?: number;
  statusText?: string;
  /**
   * What actually failed, for the cases where the HTTP status alone reads as
   * success. A 200 carrying a per-product `error` is the motivating case: a
   * context showing only `status: 200` looks like the call worked.
   */
  outcome?: string;
  /** Parsed JSON body, or the raw text if it did not parse. */
  body?: unknown;
};

export type VendorErrorContext = {
  vendor: string;
  status?: number;
  statusText?: string;
  outcome?: string;
  body: string;
};

/**
 * Short, stable, one-way discriminator for a vendor failure, used ONLY to give
 * the Sentry flood guard something to vary on.
 *
 * The guard keys events by `type:value|frame`, and every getProductRates failure
 * in a cart shares all three — so a 20-item cart's twenty DIFFERENT vendor
 * bodies collapsed into the 5 the rolling window allows, and the diagnostics
 * this whole change exists to capture were dropped for the other fifteen.
 * Hashing the body means identical diagnostics still collapse (that is the
 * guard working as intended) while genuinely different ones each report.
 *
 * A hash rather than the raw product id because this rides in a Sentry TAG,
 * which is indexed and searchable; the body itself is already in the event
 * context, so the tag needs no readable content. FNV-1a, 8 hex chars, O(body).
 */
export function vendorErrorVarianceKey(ctx: VendorErrorContext): string {
  const input = `${ctx.vendor}:${ctx.status ?? ''}:${ctx.outcome ?? ''}:${ctx.body}`;
  let hash = 0x811c9dc5;
  for (let i = 0; i < input.length; i += 1) {
    hash ^= input.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193);
  }
  return (hash >>> 0).toString(16);
}

/** Exported for tests. Produces exactly what is attached to the error. */
export function buildVendorErrorContext(
  vendor: string,
  response: VendorErrorResponse
): VendorErrorContext {
  return {
    vendor,
    status: response.status,
    statusText: response.statusText,
    outcome: response.outcome,
    body: serialize(response.body),
  };
}

/**
 * Attach the redacted vendor response to `error` and return it, so a call site
 * can `throw logVendorError(new Error(msg), 'vendor', { ... })`.
 */
export function logVendorError<E>(
  error: E,
  vendor: string,
  response: VendorErrorResponse
): E {
  try {
    Object.defineProperty(error as object, VENDOR_ERROR_PROP, {
      value: buildVendorErrorContext(vendor, response),
      enumerable: false,
      configurable: true,
      writable: true,
    });
  } catch {
    // Reporting must never mask the caller's real error.
  }
  return error;
}

/** Read the attached context, if any. Used by logError at capture time. */
export function getVendorErrorContext(
  error: unknown
): VendorErrorContext | undefined {
  try {
    // hasOwnProperty, not a bare read: a polluted Object.prototype would
    // otherwise attach a forged vendor_error context to every captured error.
    if (!error || !Object.prototype.hasOwnProperty.call(error, VENDOR_ERROR_PROP)) {
      return undefined;
    }
    const ctx = (error as Record<string, unknown>)[VENDOR_ERROR_PROP];
    return ctx && typeof ctx === 'object' ? (ctx as VendorErrorContext) : undefined;
  } catch {
    return undefined;
  }
}
